import * as XLSX from 'xlsx';
import { House, User } from '../services/types';
import { formatDate } from './reservationHelpers';

/**
 * Export houses data to Excel file
 * @param houses - Array of houses to export
 * @param users - Array of users for name lookup
 * @param filename - Optional filename (defaults to "houses_export_{date}.xlsx")
 */
export const exportHousesToExcel = (
    houses: House[],
    users: User[],
    filename?: string
): void => {
    // Helper function to get user name
    const getUserName = (userId: string | null | undefined): string => {
        if (!userId) return 'N/A';
        const user = users.find(u => u.public_id === userId);
        return user ? user.nome : 'Unknown User';
    };

    // Helper function to format currency
    const formatCurrency = (value: number | null | undefined): string => {
        if (value === null || value === undefined) return '-';
        return `€${value.toFixed(2)}`;
    };

    // Transform houses data into Excel-friendly format
    const data = houses.map((house) => ({
        'House ID': house.public_id,
        'Name': house.name || '-',
        'Address': house.address,
        'Location': house.location,
        'Post Code': house.post_code,
        'Color': house.color || '-',
        'Value per Service': formatCurrency(house.value_per_service),
        'Website': house.website || '-',
        'Owner/User': getUserName(house.user_id),
        'User ID': house.user_id || '-',
        'Observations': house.observation || '-',
        'Created At': formatDate(house.created_at, 'MMM dd, yyyy HH:mm'),
        'Updated At': formatDate(house.updated_at, 'MMM dd, yyyy HH:mm'),
    }));

    // Create a new workbook
    const wb = XLSX.utils.book_new();

    // Convert data to worksheet
    const ws = XLSX.utils.json_to_sheet(data);

    // Set column widths for better readability
    const colWidths = [
        { wch: 30 }, // House ID
        { wch: 25 }, // Name
        { wch: 35 }, // Address
        { wch: 20 }, // Location
        { wch: 12 }, // Post Code
        { wch: 12 }, // Color
        { wch: 18 }, // Value per Service
        { wch: 30 }, // Website
        { wch: 25 }, // Owner/User
        { wch: 30 }, // User ID
        { wch: 40 }, // Observations
        { wch: 20 }, // Created At
        { wch: 20 }, // Updated At
    ];
    ws['!cols'] = colWidths;

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, 'Houses');

    // Generate filename with current date if not provided
    const defaultFilename = `houses_export_${new Date().toISOString().split('T')[0]}.xlsx`;
    const finalFilename = filename || defaultFilename;

    // Write file
    XLSX.writeFile(wb, finalFilename);
};
